import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";

/**
 * Patient text routing endpoints (INBOX_TEXT_ROUTING_FEATURE). Nothing here may
 * be called with the flag off — every caller gates through
 * `useTextRoutingEnabled` (counts / done: routing on; clinic owners: flag on).
 */

export type TextRoutingCounts = {
  mine: number;
  my_clinics: number;
  unassigned: number;
  // Every waiting conversation in scope — the All conversations tab's count.
  all: number;
  // The caller is the inbox owner of at least one active clinic; the My clinics tab hides otherwise.
  owns_clinics: boolean;
};

export type TextRoutingCountsArgs = {
  preferred_clinic?: number[];
  region?: number;
};

export type SetClinicOwnersArgs = {
  clinic_ids: number[];
  user_id: number | null;
};

/** One row of `GET /text-routing/region-owners/`; `region_id: null` is the clinics without a region. */
export type RegionInboxOwnerRow = {
  region_id: number | null;
  region_name: string | null;
  active_count: number;
  inactive_count: number;
  // Clinics with no inbox owner (a deactivated owner counts as none), over every
  // clinic and over the active ones only — only active clinics take texts.
  unowned_count: number;
  unowned_active_count: number;
  owners: {
    id: number;
    name: string;
    clinic_count: number;
    active_clinic_count: number;
  }[];
};

export type RegionInboxOwnersResponse = { regions: RegionInboxOwnerRow[] };

export type SetRegionOwnersArgs = {
  // A region row only sets an owner (never clears one), so `user_id` is required.
  assignments: { region_id: number | null; user_id: number }[];
  include_inactive: boolean;
};

export const textRoutingApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    // Counts of conversations WAITING for a staff reply, in the same
    // preferred_clinic / region scope the inbox list uses. Also provides
    // `PatientDialogs` on purpose: every existing invalidation of the dialog
    // list (assign, read, tag edits, socket refreshes) then refreshes the tab
    // counts and the side-menu badge with it, without touching each caller.
    getTextRoutingCounts: builder.query<
      TextRoutingCounts,
      TextRoutingCountsArgs
    >({
      query: ({ preferred_clinic, region }) =>
        `/text-routing/counts/${formatValidQueryParamUrlCommaSeparated(
          { preferred_clinic, region },
          true
        )}`,
      providesTags: ["TextRoutingCounts", "PatientDialogs"]
    }),
    setClinicInboxOwners: builder.mutation<
      { updated: number },
      SetClinicOwnersArgs
    >({
      query: (body) => ({
        url: "/text-routing/clinic-owners/",
        method: "POST",
        body
      }),
      invalidatesTags: ["Clinics", "TextRoutingCounts", "UnansweredTexts"]
    }),
    // Administration → Clinics "Set owners by region": per-region clinic counts and
    // current owners. Provides `Clinics` so every owner save refreshes the rows.
    getRegionInboxOwners: builder.query<RegionInboxOwnersResponse, void>({
      query: () => "/text-routing/region-owners/",
      providesTags: ["Clinics"]
    }),
    setRegionInboxOwners: builder.mutation<
      { updated: number },
      SetRegionOwnersArgs
    >({
      query: (body) => ({
        url: "/text-routing/region-owners/",
        method: "POST",
        body
      }),
      invalidatesTags: ["Clinics", "TextRoutingCounts", "UnansweredTexts"]
    }),
    markInboxDone: builder.mutation<void, { patientId: number }>({
      query: ({ patientId }) => ({
        url: `/text-routing/patients/${patientId}/done/`,
        method: "POST"
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        "PatientDialogs",
        "TextRoutingCounts",
        // The Unanswered Texts report / Inbox overview count the same waiting rows.
        "UnansweredTexts",
        { type: "Patient" as const, id: patientId }
      ]
    })
  })
});

export const {
  useGetTextRoutingCountsQuery,
  useSetClinicInboxOwnersMutation,
  useGetRegionInboxOwnersQuery,
  useSetRegionInboxOwnersMutation,
  useMarkInboxDoneMutation
} = textRoutingApi;
